/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  CheckCircle2, Clock, AlertTriangle, Image as ImageIcon, Film,
  Music2, Loader2, RefreshCcw, Download, FileText, Link as LinkIcon, Youtube,
} from 'lucide-react';
import { displayNameFor } from '@/lib/displayName';

/**
 * ReferencesPanel
 * ────────────────
 * Pulls /api/me/references for the logged-in artist and renders:
 *   • Concept Art cards  — every state, with a coloured status pill so the
 *     animatics artist can tell at a glance whether the concept is locked
 *     ("APPROVED") or still in flux ("PENDING APPROVAL" / "CHANGES REQUESTED").
 *   • VO cards           — only ones the client has approved (studio rule).
 *
 * Each card auto-fetches the first media's authenticated blob so we render
 * a true thumbnail / inline audio player rather than a placeholder icon.
 */
export default function ReferencesPanel({ defaultTab = 'concept' }) {
  const { api } = useAuth();
  const [data, setData] = useState({ concept_art: [], vo: [], projects: [], scripts: [], reference_links: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(defaultTab); // 'concept' | 'vo' | 'script' | 'links'
  const [conceptKind, setConceptKind] = useState('all'); // 'all'|'character'|'environment'|'prop'
  const [projectFilter, setProjectFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/me/references');
      setData(r.data || { concept_art: [], vo: [], projects: [], scripts: [], reference_links: [] });
    } catch {
      setData({ concept_art: [], vo: [], projects: [], scripts: [], reference_links: [] });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filterByProject = (arr) => projectFilter === 'all' ? arr : arr.filter(s => s.project_id === projectFilter);
  const concept = useMemo(() => filterByProject(data.concept_art), [data, projectFilter]);
  const vo = useMemo(() => filterByProject(data.vo), [data, projectFilter]);
  const scripts = useMemo(() => filterByProject(data.scripts || []), [data, projectFilter]);
  const refLinks = useMemo(() => filterByProject(data.reference_links || []), [data, projectFilter]);

  const conceptByKind = useMemo(() => {
    const out = { all: concept, character: [], environment: [], prop: [], other: [] };
    for (const c of concept) {
      const k = (c.subkind || '').toLowerCase();
      if (k === 'character' || k === 'environment' || k === 'prop') out[k].push(c);
      else out.other.push(c);
    }
    return out;
  }, [concept]);
  const conceptVisible = conceptByKind[conceptKind] || concept;

  const approvedCount = concept.filter(c => c.approval_state === 'approved').length;
  const pendingCount = concept.filter(c => c.approval_state === 'pending').length;

  return (
    <div className="space-y-4" data-testid="references-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-heading font-bold text-white">References</h2>
          <p className="text-[10px] text-zinc-500 mt-0.5">
            Concept art &amp; voice-over uploaded by Production for shots assigned to you.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.projects.length > 1 && (
            <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}
              data-testid="ref-project-filter"
              className="h-8 px-2 bg-zinc-900 border border-zinc-800 rounded-sm text-[11px] text-white focus:outline-none">
              <option value="all">All projects ({data.projects.length})</option>
              {data.projects.map(p => <option key={p.id} value={p.id}>{p.name || p.id.slice(0, 8)}</option>)}
            </select>
          )}
          <button onClick={load} disabled={loading} data-testid="ref-refresh"
            className="h-8 px-3 bg-zinc-900 border border-zinc-800 rounded-sm text-[10px] text-zinc-300 hover:text-white flex items-center gap-1.5 disabled:opacity-40">
            <RefreshCcw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Sub tabs */}
      <div className="flex gap-1 border-b border-zinc-800">
        <button onClick={() => setTab('concept')} data-testid="ref-tab-concept"
          className={`px-3 py-2 text-[11px] font-medium border-b-2 -mb-px ${tab === 'concept' ? 'border-pink-400 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          Concept Art
          <span className="ml-2 text-[9px] font-mono text-zinc-500">{concept.length}</span>
          {pendingCount > 0 && <span className="ml-1 text-[9px] text-amber-400">· {pendingCount} pending</span>}
          {approvedCount > 0 && <span className="ml-1 text-[9px] text-emerald-400">· {approvedCount} approved</span>}
        </button>
        <button onClick={() => setTab('vo')} data-testid="ref-tab-vo"
          className={`px-3 py-2 text-[11px] font-medium border-b-2 -mb-px ${tab === 'vo' ? 'border-yellow-400 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          Voice-Over (Approved)
          <span className="ml-2 text-[9px] font-mono text-zinc-500">{vo.length}</span>
        </button>
        <button onClick={() => setTab('script')} data-testid="ref-tab-script"
          className={`px-3 py-2 text-[11px] font-medium border-b-2 -mb-px ${tab === 'script' ? 'border-cyan-400 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          Script &amp; Brief
          <span className="ml-2 text-[9px] font-mono text-zinc-500">{scripts.length}</span>
        </button>
        <button onClick={() => setTab('links')} data-testid="ref-tab-links"
          className={`px-3 py-2 text-[11px] font-medium border-b-2 -mb-px ${tab === 'links' ? 'border-orange-400 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          Reference Links
          <span className="ml-2 text-[9px] font-mono text-zinc-500">{refLinks.reduce((s, p) => s + p.links.length, 0)}</span>
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-xs py-12 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading references…
        </div>
      ) : tab === 'concept' ? (
        concept.length === 0 ? (
          <EmptyState
            icon={<ImageIcon className="w-6 h-6 text-zinc-700" />}
            title="No concept art yet"
            sub="Production hasn't uploaded any concept art for your shots yet. They'll appear here automatically once submitted."
          />
        ) : (
          <>
            {/* Character | Environment | Props segregation — same buckets the
                PM uploads under via Submit-for-Approval > Concept Art. */}
            <div className="flex gap-1 -mt-1 mb-1" data-testid="ref-concept-subtabs">
              {[
                { k: 'all',         label: 'All',          count: conceptByKind.all.length },
                { k: 'character',   label: 'Characters',   count: conceptByKind.character.length },
                { k: 'environment', label: 'Environments', count: conceptByKind.environment.length },
                { k: 'prop',        label: 'Props',        count: conceptByKind.prop.length },
                ...(conceptByKind.other.length > 0
                  ? [{ k: 'other', label: 'Other', count: conceptByKind.other.length }]
                  : []),
              ].map(t => (
                <button key={t.k} onClick={() => setConceptKind(t.k)}
                  data-testid={`ref-concept-sub-${t.k}`}
                  className={`px-2.5 py-1 rounded-sm text-[10px] font-medium uppercase tracking-wider transition-colors ${conceptKind === t.k ? 'bg-pink-500/20 border border-pink-400/40 text-pink-100' : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}>
                  {t.label}
                  <span className={`ml-1.5 text-[9px] font-mono ${conceptKind === t.k ? 'text-pink-300' : 'text-zinc-600'}`}>{t.count}</span>
                </button>
              ))}
            </div>
            {conceptVisible.length === 0 ? (
              <EmptyState
                icon={<ImageIcon className="w-6 h-6 text-zinc-700" />}
                title={`No ${conceptKind === 'all' ? '' : conceptKind + ' '}concept art yet`}
                sub="Switch to a different category, or wait for Production to upload."
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {conceptVisible.map(c => <ReferenceCard key={c.id} sub={c} kind="concept" api={api} />)}
              </div>
            )}
          </>
        )
      ) : tab === 'vo' ? (
        vo.length === 0 ? (
          <EmptyState
            icon={<Music2 className="w-6 h-6 text-zinc-700" />}
            title="No approved VO yet"
            sub="Once the client approves a voice-over track, it will show up here so you can sync your animatic to it."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {vo.map(v => <ReferenceCard key={v.id} sub={v} kind="vo" api={api} />)}
          </div>
        )
      ) : tab === 'script' ? (
        scripts.length === 0 ? (
          <EmptyState
            icon={<FileText className="w-6 h-6 text-zinc-700" />}
            title="No script or brief uploaded yet"
            sub="Production hasn't uploaded a script for your project yet. Once they do, it'll appear here so you can read along while you work."
          />
        ) : (
          <div className="space-y-3" data-testid="ref-scripts">
            {scripts.map(s => (
              <div key={s.project_id} className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
                <p className="text-[10px] uppercase tracking-wider text-cyan-400 mb-1">{s.project_name}</p>
                {s.logline && <p className="text-xs text-zinc-200 italic mb-2">"{s.logline}"</p>}
                {s.synopsis && (
                  <div className="mb-2">
                    <p className="text-[9px] uppercase text-zinc-500 mb-1">Synopsis</p>
                    <p className="text-xs text-zinc-300 whitespace-pre-wrap">{s.synopsis}</p>
                  </div>
                )}
                {s.script && (
                  <div>
                    <p className="text-[9px] uppercase text-zinc-500 mb-1">Script / Screenplay</p>
                    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 max-h-96 overflow-y-auto">
                      <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">{s.script}</pre>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        refLinks.length === 0 ? (
          <EmptyState
            icon={<LinkIcon className="w-6 h-6 text-zinc-700" />}
            title="No reference links yet"
            sub="Production / client hasn't shared any reference links (YouTube, mood boards, etc.) yet."
          />
        ) : (
          <div className="space-y-3" data-testid="ref-links">
            {refLinks.map(p => (
              <div key={p.project_id} className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
                <p className="text-[10px] uppercase tracking-wider text-orange-400 mb-2">{p.project_name}</p>
                <ul className="space-y-1.5">
                  {p.links.map((l, i) => (
                    <li key={i}>
                      <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`ref-link-${i}`}
                        className="flex items-center gap-2 text-xs text-zinc-300 hover:text-white group">
                        {l.type === 'youtube' ? <Youtube className="w-3.5 h-3.5 text-red-400 flex-shrink-0" /> : <LinkIcon className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />}
                        <span className="truncate group-hover:underline">{l.title || l.url}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function EmptyState({ icon, title, sub }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-md py-12 text-center" data-testid="ref-empty">
      <div className="inline-flex w-12 h-12 rounded-full bg-zinc-800/60 items-center justify-center mb-3">{icon}</div>
      <p className="text-sm text-zinc-300 font-medium">{title}</p>
      <p className="text-[10px] text-zinc-500 mt-1 max-w-md mx-auto">{sub}</p>
    </div>
  );
}

function ReferenceCard({ sub, kind, api }) {
  const { user } = useAuth();
  const viewerRole = user?.role;
  const first = sub.media?.[0];
  const [thumbUrl, setThumbUrl] = useState('');
  const [audioUrl, setAudioUrl] = useState('');

  useEffect(() => {
    if (!first?.storage_key) return;
    let cancelled = false;
    let blobUrl = '';
    (async () => {
      try {
        const r = await api.get(`/reviews/media?key=${encodeURIComponent(first.storage_key)}`, { responseType: 'blob' });
        if (cancelled) return;
        blobUrl = URL.createObjectURL(r.data);
        if ((first.type || '').startsWith('audio') || /\.(mp3|wav|m4a|ogg|aac|flac)$/i.test(first.filename || '')) {
          setAudioUrl(blobUrl);
        } else {
          setThumbUrl(blobUrl);
        }
      } catch { /* fall back to icon */ }
    })();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [first?.storage_key]);

  const isVideo = first?.type === 'video';
  const fileExt = (first?.filename || '').split('.').pop()?.toLowerCase() || '';
  const isAudio = (first?.type || '').startsWith('audio') || ['mp3', 'wav', 'm4a', 'ogg', 'aac', 'flac'].includes(fileExt);

  const pill = APPROVAL_PILLS[sub.approval_state] || APPROVAL_PILLS.pending;
  const accent = kind === 'concept' ? 'from-pink-500/15 to-fuchsia-500/5 border-pink-500/20' : 'from-yellow-500/15 to-amber-500/5 border-yellow-500/20';

  return (
    <div className={`group bg-zinc-900 border rounded-md overflow-hidden hover:border-zinc-700 transition-colors bg-gradient-to-br ${accent}`}
      data-testid={`ref-card-${sub.id}`}>
      {/* Thumb / player */}
      {isAudio ? (
        <div className="bg-black/40 p-3 border-b border-zinc-800">
          <div className="flex items-center gap-2 mb-2">
            <Music2 className="w-3.5 h-3.5 text-yellow-300" />
            <span className="text-[10px] text-zinc-400 truncate flex-1">{first.filename}</span>
          </div>
          {audioUrl ? (
            <audio src={audioUrl} controls className="w-full" style={{ height: 28 }} data-testid={`ref-audio-${sub.id}`} />
          ) : (
            <div className="text-[10px] text-zinc-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading audio…</div>
          )}
        </div>
      ) : (
        <div className="aspect-video bg-black flex items-center justify-center relative overflow-hidden">
          {thumbUrl ? (
            isVideo ? (
              <video src={thumbUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" />
            ) : (
              <img src={thumbUrl} alt={sub.title} className="w-full h-full object-cover" data-testid={`ref-img-${sub.id}`} />
            )
          ) : (
            isVideo ? <Film className="h-8 w-8 text-zinc-600" /> : <ImageIcon className="h-8 w-8 text-zinc-600" />
          )}
          {sub.media?.length > 1 && (
            <span className="absolute top-2 right-2 text-[9px] bg-black/70 text-white px-1.5 py-0.5 rounded">+{sub.media.length - 1}</span>
          )}
        </div>
      )}
      {/* Body */}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h3 className="text-xs font-semibold text-white truncate flex-1">{sub.title}</h3>
          <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded flex items-center gap-1 flex-shrink-0 ${pill.cls}`}
            data-testid={`ref-pill-${sub.id}`} title={pill.title}>
            {pill.icon}
            {pill.label}
          </span>
        </div>
        <p className="text-[10px] text-zinc-500 truncate flex items-center gap-1.5">
          {sub.parent_codes?.[0] && (
            <span className="text-[9px] font-mono text-pink-300/80 bg-pink-500/10 border border-pink-500/20 px-1 rounded">
              {sub.parent_codes[0]}
            </span>
          )}
          {sub.project_name || '—'} · {displayNameFor(sub, viewerRole)} · R{sub.round}
        </p>
        {sub.description && (
          <p className="text-[10px] text-zinc-400 mt-1 line-clamp-2">{sub.description}</p>
        )}
      </div>
    </div>
  );
}

const APPROVAL_PILLS = {
  approved: {
    label: 'Approved',
    cls: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40',
    icon: <CheckCircle2 className="w-2.5 h-2.5" />,
    title: 'Greenlit by client — safe to use as locked reference.',
  },
  pending: {
    label: 'Pending',
    cls: 'bg-amber-500/20 text-amber-300 border border-amber-500/40',
    icon: <Clock className="w-2.5 h-2.5" />,
    title: 'Sitting with client / supervisor — may still change.',
  },
  changes_requested: {
    label: 'Changes',
    cls: 'bg-rose-500/20 text-rose-300 border border-rose-500/40',
    icon: <AlertTriangle className="w-2.5 h-2.5" />,
    title: 'Client asked for revisions — wait for the next round before using.',
  },
};
